import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
    AlertCircle,
    ArrowLeft,
    Building2,
    CheckCircle,
    Clock,
    Eye,
    EyeOff,
    Lock,
    LogIn,
    Mail,
    Shield,
    UserCheck
} from 'lucide-react';
import axios from 'axios';
import heLogo from '../Assets/HE.AI-Logo.png';
import { API_BASE_URL } from '../config';

const AdminLogin = ({
    apiBaseUrl = API_BASE_URL,
    redirectPath = '/dashboard',
    customLogo = null,
    backgroundImage = null
}) => {
    const [searchParams] = useSearchParams();
    const prefilledEmail = searchParams.get('email');
    const prefilledSchoolId = searchParams.get('school_id');
    const isActivation = searchParams.get('activate') === 'true';

    const [step, setStep] = useState(isActivation ? 'admin-setup' : 'credentials');
    const [credentials, setCredentials] = useState({
        email: prefilledEmail || '',
        password: '',
    });
    const [availableSchools, setAvailableSchools] = useState([]);
    const [selectedSchool, setSelectedSchool] = useState(
        prefilledSchoolId ? { id: prefilledSchoolId, name: searchParams.get('school_name') || 'Your School' } : null
    );
    const [forgotPasswordSchools, setForgotPasswordSchools] = useState([]);
    const [activationData, setActivationData] = useState({
        otp: '',
        newPassword: '',
        confirmPassword: '',
    });
    const [forgotPasswordData, setForgotPasswordData] = useState({
        email: '',
        otp: '',
        newPassword: '',
        confirmPassword: '',
        schoolId: '',
    });

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [passwordShown, setPasswordShown] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [otpTimeLeft, setOtpTimeLeft] = useState(0);
    const [canResendOtp, setCanResendOtp] = useState(false);
    const [resendCountdown, setResendCountdown] = useState(0);
    const [passwordStrength, setPasswordStrength] = useState({
        length: false,
        uppercase: false,
        lowercase: false,
        number: false,
        special: false,
        match: false
    });

    const otpTimerRef = useRef(null);
    const resendTimerRef = useRef(null);
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        const query = new URLSearchParams(location.search);
        if (query.get('expired') === 'true') {
            setError('Your session has expired. Please login again.');
        }
        return () => {
            if (otpTimerRef.current) clearInterval(otpTimerRef.current);
            if (resendTimerRef.current) clearInterval(resendTimerRef.current);
        };
    }, [location]);

    useEffect(() => {
        const newStrength = {
            length: activationData.newPassword.length >= 8,
            uppercase: /[A-Z]/.test(activationData.newPassword),
            lowercase: /[a-z]/.test(activationData.newPassword),
            number: /[0-9]/.test(activationData.newPassword),
            special: /[^A-Za-z0-9]/.test(activationData.newPassword),
            match: activationData.newPassword === activationData.confirmPassword && activationData.newPassword !== ''
        };
        setPasswordStrength(newStrength);
    }, [activationData.newPassword, activationData.confirmPassword]);

    const startOtpTimer = () => {
        if (otpTimerRef.current) clearInterval(otpTimerRef.current);
        setOtpTimeLeft(10 * 60);
        otpTimerRef.current = setInterval(() => {
            setOtpTimeLeft((prev) => {
                if (prev <= 1) {
                    clearInterval(otpTimerRef.current);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    };

    const startResendCountdown = () => {
        if (resendTimerRef.current) clearInterval(resendTimerRef.current);
        setResendCountdown(30);
        setCanResendOtp(false);
        resendTimerRef.current = setInterval(() => {
            setResendCountdown((prev) => {
                if (prev <= 1) {
                    clearInterval(resendTimerRef.current);
                    setCanResendOtp(true);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    };

    const formatTime = (seconds) => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
    };

    const handleCredentialsSubmit = async () => {
        setLoading(true);
        setError('');
        setSuccessMessage('');

        try {
            const formData = new URLSearchParams();
            formData.append('username', credentials.email.toLowerCase());
            formData.append('password', credentials.password);

            // Use admin-panel endpoint which auto-detects role from admin panel roles
            const response = await axios.post(`${apiBaseUrl}/login/admin-panel`, formData, {
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
            });

            const data = response.data;

            if (data.status === 'school_selection_required') {
                setAvailableSchools(data.schools);
                setStep('school-selection');
            } else if (data.status === 'setup_required') {
                setSelectedSchool({ id: data.school_id, name: data.school_name || 'Your School' });
                setStep('admin-setup');
            } else if (data.access_token) {
                handleLoginSuccess(data);
            } else {
                throw new Error('Unexpected response from server');
            }
        } catch (error) {
            console.error('Login error:', error);
            if (error.response?.status === 401) {
                setError('Invalid email or password. Please try again.');
            } else if (error.response?.status === 403) {
                setError('Your account is inactive. Please contact your administrator.');
            } else {
                const errorDetail = error.response?.data?.detail;
                if (typeof errorDetail === 'string') {
                    setError(errorDetail);
                } else if (Array.isArray(errorDetail)) {
                    setError(errorDetail.map(err => typeof err === 'string' ? err : err.msg || 'Validation error').join(', '));
                } else if (errorDetail && typeof errorDetail === 'object') {
                    setError(errorDetail.msg || 'Validation error occurred');
                } else {
                    setError(error.message || 'An error occurred during login');
                }
            }
        } finally {
            setLoading(false);
        }
    };

    const completeLogin = async (schoolId, schoolName) => {
        setLoading(true);
        setError('');

        try {
            const payload = {
                email: credentials.email.toLowerCase(),
                password: credentials.password,
                role: 'admin',
                school_id: schoolId
            };

            const response = await axios.post(`${apiBaseUrl}/login/complete`, payload, {
                headers: { 'Content-Type': 'application/json' }
            });

            const data = response.data;

            if (data.status === 'setup_required') {
                setSelectedSchool({ id: schoolId, name: schoolName || 'Your School' });
                setStep('admin-setup');
            } else {
                handleLoginSuccess(data);
            }
        } catch (error) {
            console.error('Login completion error:', error);
            if (error.response?.status === 403) {
                setError('Account setup required. Please activate your account first.');
            } else {
                const errorDetail = error.response?.data?.detail;
                if (typeof errorDetail === 'string') {
                    setError(errorDetail);
                } else if (Array.isArray(errorDetail)) {
                    setError(errorDetail.map(err => typeof err === 'string' ? err : err.msg || 'Validation error').join(', '));
                } else if (errorDetail && typeof errorDetail === 'object') {
                    setError(errorDetail.msg || 'Login failed');
                } else {
                    setError(error.message || 'Login failed');
                }
            }
        } finally {
            setLoading(false);
        }
    };

    const handleLoginSuccess = (data) => {
        localStorage.setItem('accessToken', data.access_token);
        localStorage.setItem('access_token', data.access_token);
        localStorage.setItem('refresh_token', data.refresh_token || '');
        localStorage.setItem('school_id', data.school_id || '');
        localStorage.setItem('school_name', data.school_name || '');

        // Extract and store user role from JWT token (supports management, admin, hod, etc.)
        try {
            const payload = JSON.parse(atob(data.access_token.split('.')[1]));
            localStorage.setItem('user_role', payload.role || 'admin');
        } catch (e) {
            localStorage.setItem('user_role', 'admin');
        }
        localStorage.setItem('user_email', credentials.email.toLowerCase());
        navigate(redirectPath);
    };

    const handleAdminSetup = async () => {
        setLoading(true);
        setError('');
        setSuccessMessage('');

        try {
            await axios.post(`${apiBaseUrl}/admin/verify-temp-password`, {
                email: credentials.email.toLowerCase(),
                temp_password: credentials.password,
                school_id: selectedSchool.id
            });

            setSuccessMessage('Verification code sent to your email');
            setStep('otp-verification');
            startOtpTimer();
            startResendCountdown();
        } catch (error) {
            console.error('Admin setup error:', error);
            const errorStatus = error.response?.status;

            if (errorStatus === 401) {
                setError('That temporary password doesn\'t match. Try again or contact support.');
            } else if (errorStatus === 404) {
                setError('Admin account not found or already activated.');
            } else if (typeof error.response?.data?.detail === 'string' && error.response?.data?.detail?.includes('expired')) {
                setError('Your temporary password expired. Ask SuperAdmin to resend the invite.');
            } else {
                const errorDetail = error.response?.data?.detail;
                if (typeof errorDetail === 'string') {
                    setError(errorDetail);
                } else if (Array.isArray(errorDetail)) {
                    setError(errorDetail.map(err => typeof err === 'string' ? err : err.msg || 'Validation error').join(', '));
                } else if (errorDetail && typeof errorDetail === 'object') {
                    setError(errorDetail.msg || 'Failed to verify temporary password');
                } else {
                    setError(error.message || 'Failed to verify temporary password');
                }
            }
        } finally {
            setLoading(false);
        }
    };

    const handleResendOTP = async () => {
        if (!canResendOtp && resendCountdown > 0) return;

        setLoading(true);
        setError('');
        setSuccessMessage('');

        try {
            await axios.post(`${apiBaseUrl}/admin/resend-otp`, {
                email: credentials.email.toLowerCase(),
                school_id: selectedSchool.id
            });

            setSuccessMessage('Verification code has been resent to your email');
            startOtpTimer();
            startResendCountdown();
        } catch (error) {
            console.error('OTP resend error:', error);
            const errorDetail = error.response?.data?.detail;
            if (typeof errorDetail === 'string') {
                setError(errorDetail);
            } else if (Array.isArray(errorDetail)) {
                setError(errorDetail.map(err => typeof err === 'string' ? err : err.msg || 'Validation error').join(', '));
            } else if (errorDetail && typeof errorDetail === 'object') {
                setError(errorDetail.msg || 'Failed to resend verification code');
            } else {
                setError(error.message || 'Failed to resend verification code');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleOTPVerification = async () => {
        if (otpTimeLeft <= 0) {
            setError('Verification code has expired. Please request a new one.');
            return;
        }

        const isStrong = passwordStrength.length && passwordStrength.uppercase &&
            passwordStrength.lowercase && passwordStrength.number && passwordStrength.special;

        if (!isStrong) {
            setError('Choose a stronger password to continue.');
            return;
        }

        if (!passwordStrength.match) {
            setError('Passwords do not match');
            return;
        }

        setLoading(true);
        setError('');
        setSuccessMessage('');

        try {
            const response = await axios.post(`${apiBaseUrl}/admin/setup-password`, {
                email: credentials.email.toLowerCase(),
                otp: parseInt(activationData.otp, 10),
                new_password: activationData.newPassword,
                school_id: selectedSchool.id
            });

            const data = response.data;

            clearInterval(otpTimerRef.current);
            clearInterval(resendTimerRef.current);

            setSuccessMessage('Account setup completed successfully!');
            setStep('activation-success');

            localStorage.setItem('accessToken', data.access_token);
            localStorage.setItem('access_token', data.access_token);
            localStorage.setItem('refresh_token', data.refresh_token || '');
            localStorage.setItem('school_id', data.school_id || '');
            localStorage.setItem('school_name', data.school_name || '');

            // Extract and store user role from JWT token
            try {
                const payload = JSON.parse(atob(data.access_token.split('.')[1]));
                localStorage.setItem('user_role', payload.role || 'admin');
            } catch (e) {
                console.warn('Failed to parse role from token, defaulting to admin', e);
                localStorage.setItem('user_role', 'admin');
            }

            localStorage.setItem('user_email', credentials.email.toLowerCase());
        } catch (error) {
            console.error('OTP verification error:', error);

            if (error.response?.status === 400) {
                setError('Invalid or expired verification code.');
            } else if (error.response?.status === 404) {
                setError('Admin account not found.');
            } else {
                const errorDetail = error.response?.data?.detail;
                if (typeof errorDetail === 'string') {
                    setError(errorDetail);
                } else if (Array.isArray(errorDetail)) {
                    setError(errorDetail.map(err => typeof err === 'string' ? err : err.msg || 'Validation error').join(', '));
                } else if (errorDetail && typeof errorDetail === 'object') {
                    setError(errorDetail.msg || 'Failed to complete account setup');
                } else {
                    setError(error.message || 'Failed to complete account setup');
                }
            }
        } finally {
            setLoading(false);
        }
    };

    const handleForgotPassword = async () => {
        if (!forgotPasswordData.email) {
            setError('Please enter your email address');
            return;
        }

        setLoading(true);
        setError('');
        setSuccessMessage('');

        try {
            // First, find schools for this email
            const response = await axios.post(`${apiBaseUrl}/password/forgot/find-schools`, {
                email: forgotPasswordData.email.toLowerCase()
            });

            const schools = response.data.schools;

            if (schools.length === 1) {
                // If only one school, proceed directly
                await sendPasswordResetOTP(schools[0].id);
            } else {
                // Multiple schools, show selection
                setForgotPasswordSchools(schools);
                setStep('forgot-password-school-selection');
            }
        } catch (error) {
            console.error('Forgot password error:', error);
            const errorDetail = error.response?.data?.detail;
            if (typeof errorDetail === 'string') {
                setError(errorDetail);
            } else if (Array.isArray(errorDetail)) {
                setError(errorDetail.map(err => typeof err === 'string' ? err : err.msg || 'Validation error').join(', '));
            } else if (errorDetail && typeof errorDetail === 'object') {
                setError(errorDetail.msg || 'Failed to send reset code');
            } else {
                setError(error.message || 'Failed to send reset code');
            }
        } finally {
            setLoading(false);
        }
    };

    const sendPasswordResetOTP = async (schoolId) => {
        setLoading(true);
        setError('');
        setSuccessMessage('');

        try {
            await axios.post(`${apiBaseUrl}/password/forgot`, {
                email: forgotPasswordData.email.toLowerCase(),
                school_id: schoolId
            });

            setForgotPasswordData(prev => ({ ...prev, schoolId }));
            setSuccessMessage('Password reset code sent to your email');
            setStep('forgot-password-otp');
            startOtpTimer();
        } catch (error) {
            console.error('Send OTP error:', error);
            const errorDetail = error.response?.data?.detail;
            if (typeof errorDetail === 'string') {
                setError(errorDetail);
            } else if (Array.isArray(errorDetail)) {
                setError(errorDetail.map(err => typeof err === 'string' ? err : err.msg || 'Validation error').join(', '));
            } else if (errorDetail && typeof errorDetail === 'object') {
                setError(errorDetail.msg || 'Failed to send reset code');
            } else {
                setError(error.message || 'Failed to send reset code');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleResetPasswordWithOTP = async () => {
        if (otpTimeLeft <= 0) {
            setError('Reset code has expired. Please request a new one.');
            return;
        }

        if (forgotPasswordData.newPassword !== forgotPasswordData.confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        if (forgotPasswordData.newPassword.length < 8) {
            setError('Password must be at least 8 characters long');
            return;
        }

        setLoading(true);
        setError('');
        setSuccessMessage('');

        try {
            await axios.post(`${apiBaseUrl}/password/reset`, {
                email: forgotPasswordData.email.toLowerCase(),
                otp: parseInt(forgotPasswordData.otp, 10),
                new_password: forgotPasswordData.newPassword,
                school_id: forgotPasswordData.schoolId || ''
            });

            setSuccessMessage('Password reset successful! You can now login with your new password.');
            setStep('credentials');
            setForgotPasswordData({ email: '', otp: '', newPassword: '', confirmPassword: '', schoolId: '' });
            clearInterval(otpTimerRef.current);
        } catch (error) {
            console.error('Password reset error:', error);
            const errorDetail = error.response?.data?.detail;
            if (typeof errorDetail === 'string') {
                setError(errorDetail);
            } else if (Array.isArray(errorDetail)) {
                setError(errorDetail.map(err => typeof err === 'string' ? err : err.msg || 'Validation error').join(', '));
            } else if (errorDetail && typeof errorDetail === 'object') {
                setError(errorDetail.msg || 'Failed to reset password');
            } else {
                setError(error.message || 'Failed to reset password');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleBackToCredentials = () => {
        setStep('credentials');
        setAvailableSchools([]);
        setForgotPasswordSchools([]);
        setSelectedSchool(prefilledSchoolId ? {
            id: prefilledSchoolId,
            name: searchParams.get('school_name') || 'Your School'
        } : null);
        setActivationData({ otp: '', newPassword: '', confirmPassword: '' });
        setForgotPasswordData({ email: '', otp: '', newPassword: '', confirmPassword: '', schoolId: '' });
        setError('');
        setSuccessMessage('');

        if (otpTimerRef.current) clearInterval(otpTimerRef.current);
        if (resendTimerRef.current) clearInterval(resendTimerRef.current);
    };

    const handleBackToAdminSetup = () => {
        setStep('admin-setup');
        setActivationData({ otp: '', newPassword: '', confirmPassword: '' });
        setError('');
        setSuccessMessage('');

        if (otpTimerRef.current) clearInterval(otpTimerRef.current);
        if (resendTimerRef.current) clearInterval(resendTimerRef.current);
    };

    const goToForgotPassword = () => {
        setStep('forgot-password');
        setError('');
        setSuccessMessage('');
        setForgotPasswordData(prev => ({ ...prev, email: credentials.email }));
    };

    const goToDashboard = () => {
        navigate(redirectPath);
    };

    const handleKeyPress = (e, callback) => {
        if (e.key === 'Enter') {
            callback();
        }
    };

    return (
        <div className="min-h-screen text-gray-800 font-['Poppins',sans-serif]">
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap');
                
                .bg-slideshow { position: fixed; inset: 0; z-index: 0; overflow: hidden; }
                .bg-slide {
                    position: absolute; inset: 0;
                    background-size: cover; background-position: center;
                    opacity: 0; animation: bgfade 15s infinite ease-in-out;
                    will-change: opacity;
                }
                .bg-slide:nth-child(1) { animation-delay: 0s; }
                .bg-slide:nth-child(2) { animation-delay: 2.5s; }
                .bg-slide:nth-child(3) { animation-delay: 5s; }
                .bg-slide:nth-child(4) { animation-delay: 7.5s; }
                .bg-slide:nth-child(5) { animation-delay: 10s; }
                .bg-slide:nth-child(6) { animation-delay: 12.5s; }
                
                @keyframes bgfade {
                    0% { opacity: 0; }
                    8% { opacity: 1; }
                    28% { opacity: 1; }
                    40% { opacity: 0; }
                    100% { opacity: 0; }
                }
                
                .gradient-overlay {
                    position: fixed; inset: 0; z-index: 1; pointer-events: none;
                    background: radial-gradient(1200px 800px at 15% 20%, rgba(249,115,22,0.20), transparent 60%),
                                radial-gradient(900px 700px at 85% 30%, rgba(251,146,60,0.16), transparent 60%),
                                linear-gradient(120deg, rgba(234,88,12,0.08), rgba(249,115,22,0.06), rgba(234,88,12,0.08));
                }
                
                .top-white-fade {
                    position: fixed; inset: 0; z-index: 2; pointer-events: none;
                    background: linear-gradient(to bottom,
                                rgba(255,255,255,0.98) 0%,
                                rgba(255,255,255,0.90) 12%,
                                rgba(255,255,255,0.72) 24%,
                                rgba(255,255,255,0.38) 42%,
                                rgba(255,255,255,0.10) 60%,
                                rgba(255,255,255,0.00) 75%);
                }
                
                .panel-glass {
                    background: rgba(255,255,255,0.97);
                    border: 1px solid rgba(255,255,255,0.35);
                }
                
                .soft-shadow {
                    box-shadow: 0 34px 68px -24px rgba(0,0,0,0.40),
                                0 22px 44px -26px rgba(249,115,22,0.22);
                }
                
                .focus-lift:focus-within {
                    transform: translateY(-1px);
                    box-shadow: 0 10px 30px rgba(249, 115, 22, 0.18);
                }
                
                @keyframes fadeInUp {
                    from { opacity: 0; transform: translateY(22px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                
                @keyframes slideInLeft {
                    from { opacity: 0; transform: translateX(-28px); }
                    to { opacity: 1; transform: translateX(0); }
                }
                
                .animate-fadeInUp { animation: fadeInUp 0.6s ease-out both; }
                .animate-slideInLeft { animation: slideInLeft 0.6s ease-out both; }
                
                .marquee-outer { position: relative; overflow: hidden; width: 100%; }
                .marquee-track {
                    display: flex; width: max-content;
                    animation: scroll-left 23.4s linear infinite;
                    color: #ea580c;
                }
                .marquee-chunk { white-space: nowrap; }
                
                @keyframes scroll-left {
                    0% { transform: translateX(0); }
                    100% { transform: translateX(-50%); }
                }
            `}</style>

            {/* Background layers */}
            <div className="bg-slideshow" aria-hidden="true">
                <div className="bg-slide"
                    style={{ backgroundImage: "url('https://s3.ap-south-1.amazonaws.com/awsimages.imagesbazaar.com/1200x1800-old/13794/SM496503.jpg')" }}></div>
                <div className="bg-slide"
                    style={{ backgroundImage: "url('https://s3.ap-south-1.amazonaws.com/awsimages.imagesbazaar.com/1200x1800-old/12135/SM377070.jpg')" }}></div>
                <div className="bg-slide"
                    style={{ backgroundImage: "url('https://s3.ap-south-1.amazonaws.com/awsimages.imagesbazaar.com/1200x1800-old/19549/SM940390.jpg')" }}></div>
                <div className="bg-slide"
                    style={{ backgroundImage: "url('https://s3.ap-south-1.amazonaws.com/awsimages.imagesbazaar.com/1200x1800-old/14229/SM523032.jpg')" }}></div>
                <div className="bg-slide"
                    style={{ backgroundImage: "url('https://s3.ap-south-1.amazonaws.com/awsimages.imagesbazaar.com/1200x1800-old/17072/SM743851.jpg')" }}></div>
                <div className="bg-slide"
                    style={{ backgroundImage: "url('https://s3.ap-south-1.amazonaws.com/awsimages.imagesbazaar.com/1200x1800-old/14550/SM543105.jpg')" }}></div>
            </div>
            <div className="gradient-overlay" aria-hidden="true"></div>
            <div className="top-white-fade" aria-hidden="true"></div>

            {/* Header */}
            <header className="relative z-10">
                <div className="max-w-8xl mx-auto px-6 py-6 flex items-center justify-between">
                    <div className="flex items-center gap-4 animate-slideInLeft">
                        <img
                            src={customLogo || heLogo}
                            alt="Logo"
                            className="h-20 md:h-24 w-auto object-contain drop-shadow"
                        />
                    </div>
                    <div className="hidden md:block text-right">
                        <p className="text-gray-900 text-base md:text-lg font-medium">Let's build National Assets
                            together</p>
                        <p className="text-gray-700 text-sm">Empowering administrators, inspiring futures</p>
                    </div>
                </div>
            </header>

            {/* Main */}
            <main className="relative z-10">
                <section className="min-h-[calc(100vh-88px)] flex items-center justify-center px-4 pb-10">
                    <div className="w-full max-w-md">
                        <div className="panel-glass soft-shadow rounded-2xl p-8 animate-fadeInUp">
                            {/* Title based on stage */}
                            <div className="text-center mb-4">
                                <h3 className="text-base font-semibold text-orange-600">
                                    {step === 'credentials' && 'Empowering Administrators for #ViksitBharat2047'}
                                    {step === 'school-selection' && 'Your Administrative Access'}
                                    {step === 'admin-setup' && 'Secure Your Admin Account'}
                                    {step === 'otp-verification' && 'Complete Your Activation'}
                                    {step === 'activation-success' && 'Welcome to the Team!'}
                                    {step === 'forgot-password' && 'Reset Your Password'}
                                    {step === 'forgot-password-school-selection' && 'Select Your School'}
                                    {step === 'forgot-password-otp' && 'Create New Password'}
                                </h3>
                            </div>

                            {/* Icon + Badge */}
                            <div className="text-center mb-6">
                                <img
                                    src="https://www.svgrepo.com/show/421614/admin-user-web.svg"
                                    alt="Admin Icon"
                                    className="mx-auto w-[108px] h-[108px] mb-0"
                                />
                                <div
                                    className="inline-block bg-blue-600 text-white px-5 py-1.5 rounded-full shadow -mt-1">
                                    <span className="text-[15px] font-semibold">
                                        {step === 'credentials' && 'Admin Login'}
                                        {step === 'school-selection' && 'Select School'}
                                        {step === 'admin-setup' && 'Account Activation'}
                                        {step === 'otp-verification' && 'Verify Account'}
                                        {step === 'activation-success' && 'Setup Complete'}
                                        {step === 'forgot-password' && 'Password Recovery'}
                                        {step === 'forgot-password-school-selection' && 'School Selection'}
                                        {step === 'forgot-password-otp' && 'Reset Password'}
                                    </span>
                                </div>
                            </div>

                            {/* Error and Success Messages */}
                            {error && (
                                <div
                                    className="mb-6 p-3 bg-red-50 border border-red-200 text-red-800 rounded-lg text-sm flex items-start gap-2">
                                    <AlertCircle size={18} className="flex-shrink-0 mt-0.5" />
                                    <span>{error}</span>
                                </div>
                            )}

                            {successMessage && (
                                <div
                                    className="mb-6 p-3 bg-green-50 border border-green-200 text-green-800 rounded-lg text-sm flex items-start gap-2">
                                    <CheckCircle size={18} className="flex-shrink-0 mt-0.5" />
                                    <span>{successMessage}</span>
                                </div>
                            )}

                            {/* CREDENTIALS LOGIN */}
                            {step === 'credentials' && (
                                <div className="space-y-5">
                                    <div className="space-y-2 focus-lift transition-transform duration-200">
                                        <label htmlFor="email"
                                            className="block text-sm font-medium text-gray-700">Email</label>
                                        <div className="relative">
                                            <span className="absolute inset-y-0 left-3 grid place-items-center">
                                                <Mail className="w-5 h-5 text-gray-400" />
                                            </span>
                                            <input
                                                id="email"
                                                type="email"
                                                value={credentials.email}
                                                onChange={(e) => setCredentials({
                                                    ...credentials,
                                                    email: e.target.value
                                                })}
                                                onKeyPress={(e) => handleKeyPress(e, handleCredentialsSubmit)}
                                                className="w-full pl-10 pr-3 py-3 rounded-lg border border-gray-300 outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition"
                                                placeholder="Enter your email"
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div className="space-y-2 focus-lift transition-transform duration-200">
                                        <label htmlFor="password"
                                            className="block text-sm font-medium text-gray-700">Password</label>
                                        <div className="relative">
                                            <span className="absolute inset-y-0 left-3 grid place-items-center">
                                                <Lock className="w-5 h-5 text-gray-400" />
                                            </span>
                                            <input
                                                id="password"
                                                type={passwordShown ? "text" : "password"}
                                                value={credentials.password}
                                                onChange={(e) => setCredentials({
                                                    ...credentials,
                                                    password: e.target.value
                                                })}
                                                onKeyPress={(e) => handleKeyPress(e, handleCredentialsSubmit)}
                                                className="w-full pl-10 pr-10 py-3 rounded-lg border border-gray-300 outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition"
                                                placeholder="Enter your password"
                                                required
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setPasswordShown(!passwordShown)}
                                                className="absolute inset-y-0 right-2 grid place-items-center px-2 rounded-md hover:bg-gray-100 transition"
                                            >
                                                {passwordShown ? <EyeOff className="w-5 h-5 text-gray-400" /> :
                                                    <Eye className="w-5 h-5 text-gray-400" />}
                                            </button>
                                        </div>
                                    </div>

                                    <div className="flex items-center justify-end text-sm">
                                        <button
                                            type="button"
                                            onClick={goToForgotPassword}
                                            className="text-orange-600 hover:text-orange-700 font-medium"
                                        >
                                            Forgot password?
                                        </button>
                                    </div>

                                    <button
                                        type="button"
                                        onClick={handleCredentialsSubmit}
                                        disabled={loading}
                                        className="w-full inline-flex items-center justify-center gap-2 bg-orange-600 hover:bg-orange-700 text-white py-3 px-4 rounded-lg font-semibold shadow-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 transition disabled:opacity-70 disabled:cursor-not-allowed"
                                    >
                                        {loading ? (
                                            <div
                                                className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                        ) : (
                                            <LogIn className="w-5 h-5" />
                                        )}
                                        {loading ? 'Signing in...' : 'Sign In'}
                                    </button>
                                </div>
                            )}

                            {/* SCHOOL SELECTION */}
                            {step === 'school-selection' && (
                                <div className="space-y-4">
                                    <div
                                        className="mb-6 p-3 bg-blue-50 border border-blue-200 text-blue-700 rounded-lg text-sm">
                                        You have administrative access to multiple schools. Please select one.
                                    </div>
                                    <div className="space-y-3 max-h-60 overflow-y-auto">
                                        {availableSchools.map(school => (
                                            <div
                                                key={school.id}
                                                className={`p-4 border-2 rounded-lg cursor-pointer transition-all duration-200 hover:border-orange-500 hover:bg-orange-50 ${loading ? 'opacity-50 cursor-not-allowed' : 'border-gray-200'
                                                    }`}
                                                onClick={() => !loading && completeLogin(school.id, school.name)}
                                            >
                                                <div className="flex items-center gap-3">
                                                    <div
                                                        className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                                                        <Building2 size={20} className="text-orange-600" />
                                                    </div>
                                                    <div className="flex-1">
                                                        <h3 className="font-medium text-gray-800">{school.name}</h3>
                                                        {school.domain &&
                                                            <p className="text-sm text-gray-500">{school.domain}</p>}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    <button
                                        type="button"
                                        onClick={handleBackToCredentials}
                                        disabled={loading}
                                        className="w-full mt-6 py-3 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors flex items-center justify-center"
                                    >
                                        <ArrowLeft className="h-4 w-4 mr-2" />
                                        Back to Login
                                    </button>
                                </div>
                            )}

                            {/* ADMIN SETUP */}
                            {step === 'admin-setup' && (
                                <div className="space-y-5">
                                    <div
                                        className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-100 text-sm text-blue-700 flex items-start gap-2">
                                        <Shield className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
                                        <div>
                                            <p className="font-medium mb-1">First-time login</p>
                                            <p>Enter your temporary password to activate your account.</p>
                                        </div>
                                    </div>

                                    {!isActivation && (
                                        <div className="space-y-2 focus-lift transition-transform duration-200">
                                            <label htmlFor="activation-email"
                                                className="block text-sm font-medium text-gray-700">Email</label>
                                            <div className="relative">
                                                <span className="absolute inset-y-0 left-3 grid place-items-center">
                                                    <Mail className="w-5 h-5 text-gray-400" />
                                                </span>
                                                <input
                                                    id="activation-email"
                                                    type="email"
                                                    value={credentials.email}
                                                    onChange={(e) => setCredentials({
                                                        ...credentials,
                                                        email: e.target.value
                                                    })}
                                                    onKeyPress={(e) => handleKeyPress(e, handleAdminSetup)}
                                                    className="w-full pl-10 pr-3 py-3 rounded-lg border border-gray-300 outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition"
                                                    placeholder="Enter your email"
                                                    readOnly={!!prefilledEmail}
                                                />
                                            </div>
                                        </div>
                                    )}

                                    <div className="space-y-2 focus-lift transition-transform duration-200">
                                        <label htmlFor="temp-password"
                                            className="block text-sm font-medium text-gray-700">Temporary
                                            Password</label>
                                        <div className="relative">
                                            <span className="absolute inset-y-0 left-3 grid place-items-center">
                                                <Lock className="w-5 h-5 text-gray-400" />
                                            </span>
                                            <input
                                                id="temp-password"
                                                type={passwordShown ? "text" : "password"}
                                                value={credentials.password}
                                                onChange={(e) => setCredentials({
                                                    ...credentials,
                                                    password: e.target.value
                                                })}
                                                onKeyPress={(e) => handleKeyPress(e, handleAdminSetup)}
                                                className="w-full pl-10 pr-10 py-3 rounded-lg border border-gray-300 outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition"
                                                placeholder="Enter temporary password"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setPasswordShown(!passwordShown)}
                                                className="absolute inset-y-0 right-2 grid place-items-center px-2 rounded-md hover:bg-gray-100 transition"
                                            >
                                                {passwordShown ? <EyeOff className="w-5 h-5 text-gray-400" /> :
                                                    <Eye className="w-5 h-5 text-gray-400" />}
                                            </button>
                                        </div>
                                        <p className="text-xs text-gray-500">Provided by your administrator</p>
                                    </div>

                                    <div className="flex space-x-3">
                                        {!isActivation && (
                                            <button
                                                type="button"
                                                onClick={handleBackToCredentials}
                                                className="flex-1 py-3 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition flex items-center justify-center"
                                            >
                                                <ArrowLeft className="h-4 w-4 mr-2" />
                                                Back
                                            </button>
                                        )}
                                        <button
                                            type="button"
                                            onClick={handleAdminSetup}
                                            disabled={loading}
                                            className="flex-1 bg-orange-600 hover:bg-orange-700 text-white py-3 px-4 rounded-lg font-semibold transition disabled:opacity-70"
                                        >
                                            {loading ? 'Verifying...' : 'Send Code'}
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* OTP VERIFICATION */}
                            {step === 'otp-verification' && (
                                <div className="space-y-5">
                                    <div className="text-center mb-2">
                                        <div className="flex items-center justify-center gap-1 text-blue-600 mb-1">
                                            <Clock className="h-4 w-4" />
                                            <span
                                                className="text-sm font-medium">Expires in {formatTime(otpTimeLeft)}</span>
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-3 text-center">6-digit
                                            verification code</label>
                                        <div className="relative">
                                            <span className="absolute inset-y-0 left-3 grid place-items-center">
                                                <Shield className="w-5 h-5 text-gray-400" />
                                            </span>
                                            <input
                                                type="text"
                                                maxLength={6}
                                                value={activationData.otp}
                                                onChange={(e) => setActivationData({
                                                    ...activationData,
                                                    otp: e.target.value.replace(/[^0-9]/g, '')
                                                })}
                                                className="w-full pl-10 pr-3 py-3 rounded-lg border border-gray-300 outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition text-center text-xl font-semibold tracking-widest"
                                                placeholder="000000"
                                            />
                                        </div>
                                        <div className="text-center mt-2">
                                            <button
                                                type="button"
                                                onClick={handleResendOTP}
                                                disabled={!canResendOtp}
                                                className={`text-sm ${canResendOtp ? 'text-orange-600 hover:text-orange-700' : 'text-gray-400'}`}
                                            >
                                                {canResendOtp ? 'Resend code' : `Resend in ${formatTime(resendCountdown)}`}
                                            </button>
                                        </div>
                                    </div>

                                    <div className="space-y-2 focus-lift transition-transform duration-200">
                                        <label htmlFor="new-password"
                                            className="block text-sm font-medium text-gray-700">New Password</label>
                                        <div className="relative">
                                            <span className="absolute inset-y-0 left-3 grid place-items-center">
                                                <Lock className="w-5 h-5 text-gray-400" />
                                            </span>
                                            <input
                                                id="new-password"
                                                type={passwordShown ? "text" : "password"}
                                                value={activationData.newPassword}
                                                onChange={(e) => setActivationData({
                                                    ...activationData,
                                                    newPassword: e.target.value
                                                })}
                                                className="w-full pl-10 pr-10 py-3 rounded-lg border border-gray-300 outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition"
                                                placeholder="Create new password"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setPasswordShown(!passwordShown)}
                                                className="absolute inset-y-0 right-2 grid place-items-center px-2 rounded-md hover:bg-gray-100 transition"
                                            >
                                                {passwordShown ? <EyeOff className="w-5 h-5 text-gray-400" /> :
                                                    <Eye className="w-5 h-5 text-gray-400" />}
                                            </button>
                                        </div>
                                    </div>

                                    <div className="space-y-2 focus-lift transition-transform duration-200">
                                        <label htmlFor="confirm-password"
                                            className="block text-sm font-medium text-gray-700">Confirm
                                            Password</label>
                                        <div className="relative">
                                            <span className="absolute inset-y-0 left-3 grid place-items-center">
                                                <Lock className="w-5 h-5 text-gray-400" />
                                            </span>
                                            <input
                                                id="confirm-password"
                                                type={passwordShown ? "text" : "password"}
                                                value={activationData.confirmPassword}
                                                onChange={(e) => setActivationData({
                                                    ...activationData,
                                                    confirmPassword: e.target.value
                                                })}
                                                className="w-full pl-10 pr-3 py-3 rounded-lg border border-gray-300 outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition"
                                                placeholder="Confirm password"
                                            />
                                        </div>
                                    </div>

                                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                                        <p className="text-sm font-medium text-gray-700 mb-2">Password requirements:</p>
                                        <ul className="space-y-1">
                                            <li className={`text-xs flex items-center ${passwordStrength.length ? 'text-green-600' : 'text-gray-500'}`}>
                                                {passwordStrength.length && <CheckCircle size={12} className="mr-1" />}
                                                At least 8 characters
                                            </li>
                                            <li className={`text-xs flex items-center ${passwordStrength.uppercase ? 'text-green-600' : 'text-gray-500'}`}>
                                                {passwordStrength.uppercase &&
                                                    <CheckCircle size={12} className="mr-1" />}
                                                Uppercase letter
                                            </li>
                                            <li className={`text-xs flex items-center ${passwordStrength.lowercase ? 'text-green-600' : 'text-gray-500'}`}>
                                                {passwordStrength.lowercase &&
                                                    <CheckCircle size={12} className="mr-1" />}
                                                Lowercase letter
                                            </li>
                                            <li className={`text-xs flex items-center ${passwordStrength.number ? 'text-green-600' : 'text-gray-500'}`}>
                                                {passwordStrength.number && <CheckCircle size={12} className="mr-1" />}
                                                Number
                                            </li>
                                            <li className={`text-xs flex items-center ${passwordStrength.special ? 'text-green-600' : 'text-gray-500'}`}>
                                                {passwordStrength.special && <CheckCircle size={12} className="mr-1" />}
                                                Special character
                                            </li>
                                            <li className={`text-xs flex items-center ${passwordStrength.match ? 'text-green-600' : 'text-gray-500'}`}>
                                                {passwordStrength.match && <CheckCircle size={12} className="mr-1" />}
                                                Passwords match
                                            </li>
                                        </ul>
                                    </div>

                                    <div className="flex space-x-3">
                                        <button
                                            type="button"
                                            onClick={handleBackToAdminSetup}
                                            className="flex-1 py-3 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition flex items-center justify-center"
                                        >
                                            <ArrowLeft className="h-4 w-4 mr-2" />
                                            Back
                                        </button>
                                        <button
                                            type="button"
                                            onClick={handleOTPVerification}
                                            disabled={loading}
                                            className="flex-1 bg-orange-600 hover:bg-orange-700 text-white py-3 px-4 rounded-lg font-semibold transition disabled:opacity-70"
                                        >
                                            {loading ? 'Setting up...' : 'Activate'}
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* ACTIVATION SUCCESS */}
                            {step === 'activation-success' && (
                                <div className="text-center space-y-6">
                                    <div
                                        className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto">
                                        <UserCheck className="w-10 h-10 text-green-600" />
                                    </div>
                                    <div>
                                        <h2 className="text-2xl font-bold text-gray-900 mb-2">All Set!</h2>
                                        <p className="text-gray-600">Your admin account is ready. You can now access
                                            your dashboard.</p>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={goToDashboard}
                                        className="w-full bg-orange-600 hover:bg-orange-700 text-white py-3 px-4 rounded-lg font-semibold transition"
                                    >
                                        Go to Dashboard
                                    </button>
                                </div>
                            )}

                            {/* FORGOT PASSWORD */}
                            {step === 'forgot-password' && (
                                <div className="space-y-5">
                                    <div
                                        className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-100 text-sm text-blue-700">
                                        Enter your email to find your account and reset your password.
                                    </div>

                                    <div className="space-y-2 focus-lift transition-transform duration-200">
                                        <label htmlFor="reset-email"
                                            className="block text-sm font-medium text-gray-700">Email</label>
                                        <div className="relative">
                                            <span className="absolute inset-y-0 left-3 grid place-items-center">
                                                <Mail className="w-5 h-5 text-gray-400" />
                                            </span>
                                            <input
                                                id="reset-email"
                                                type="email"
                                                value={forgotPasswordData.email}
                                                onChange={(e) => setForgotPasswordData({
                                                    ...forgotPasswordData,
                                                    email: e.target.value
                                                })}
                                                onKeyPress={(e) => handleKeyPress(e, handleForgotPassword)}
                                                className="w-full pl-10 pr-3 py-3 rounded-lg border border-gray-300 outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition"
                                                placeholder="Enter your email"
                                            />
                                        </div>
                                    </div>

                                    <div className="flex space-x-3">
                                        <button
                                            type="button"
                                            onClick={handleBackToCredentials}
                                            className="flex-1 py-3 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition flex items-center justify-center"
                                        >
                                            <ArrowLeft className="h-4 w-4 mr-2" />
                                            Back
                                        </button>
                                        <button
                                            type="button"
                                            onClick={handleForgotPassword}
                                            disabled={loading}
                                            className="flex-1 bg-orange-600 hover:bg-orange-700 text-white py-3 px-4 rounded-lg font-semibold transition disabled:opacity-70"
                                        >
                                            {loading ? 'Finding Account...' : 'Continue'}
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* FORGOT PASSWORD SCHOOL SELECTION */}
                            {step === 'forgot-password-school-selection' && (
                                <div className="space-y-4">
                                    <div
                                        className="mb-6 p-3 bg-blue-50 border border-blue-200 text-blue-700 rounded-lg text-sm">
                                        We found your account in multiple schools. Please select the school where you want to reset your password.
                                    </div>
                                    <div className="space-y-3 max-h-60 overflow-y-auto">
                                        {forgotPasswordSchools.map(school => (
                                            <div
                                                key={school.id}
                                                className={`p-4 border-2 rounded-lg cursor-pointer transition-all duration-200 hover:border-orange-500 hover:bg-orange-50 ${loading ? 'opacity-50 cursor-not-allowed' : 'border-gray-200'
                                                    }`}
                                                onClick={() => !loading && sendPasswordResetOTP(school.id)}
                                            >
                                                <div className="flex items-center gap-3">
                                                    <div
                                                        className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                                                        <Building2 size={20} className="text-orange-600" />
                                                    </div>
                                                    <div className="flex-1">
                                                        <h3 className="font-medium text-gray-800">{school.name}</h3>
                                                        <p className="text-sm text-gray-500">Click to reset password for this school</p>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => setStep('forgot-password')}
                                        disabled={loading}
                                        className="w-full mt-6 py-3 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors flex items-center justify-center"
                                    >
                                        <ArrowLeft className="h-4 w-4 mr-2" />
                                        Back to Email Entry
                                    </button>
                                </div>
                            )}

                            {/* FORGOT PASSWORD OTP */}
                            {step === 'forgot-password-otp' && (
                                <div className="space-y-5">
                                    <div className="text-center mb-2">
                                        <div className="flex items-center justify-center gap-1 text-blue-600 mb-1">
                                            <Clock className="h-4 w-4" />
                                            <span
                                                className="text-sm font-medium">Expires in {formatTime(otpTimeLeft)}</span>
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-3 text-center">6-digit
                                            verification code</label>
                                        <div className="relative">
                                            <span className="absolute inset-y-0 left-3 grid place-items-center">
                                                <Shield className="w-5 h-5 text-gray-400" />
                                            </span>
                                            <input
                                                type="text"
                                                maxLength={6}
                                                value={forgotPasswordData.otp}
                                                onChange={(e) => setForgotPasswordData({
                                                    ...forgotPasswordData,
                                                    otp: e.target.value.replace(/[^0-9]/g, '')
                                                })}
                                                className="w-full pl-10 pr-3 py-3 rounded-lg border border-gray-300 outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition text-center text-xl font-semibold tracking-widest"
                                                placeholder="000000"
                                            />
                                        </div>
                                    </div>

                                    <div className="space-y-2 focus-lift transition-transform duration-200">
                                        <label htmlFor="reset-new-password"
                                            className="block text-sm font-medium text-gray-700">New Password</label>
                                        <div className="relative">
                                            <span className="absolute inset-y-0 left-3 grid place-items-center">
                                                <Lock className="w-5 h-5 text-gray-400" />
                                            </span>
                                            <input
                                                id="reset-new-password"
                                                type={passwordShown ? "text" : "password"}
                                                value={forgotPasswordData.newPassword}
                                                onChange={(e) => setForgotPasswordData({
                                                    ...forgotPasswordData,
                                                    newPassword: e.target.value
                                                })}
                                                className="w-full pl-10 pr-10 py-3 rounded-lg border border-gray-300 outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition"
                                                placeholder="Create new password"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setPasswordShown(!passwordShown)}
                                                className="absolute inset-y-0 right-2 grid place-items-center px-2 rounded-md hover:bg-gray-100 transition"
                                            >
                                                {passwordShown ? <EyeOff className="w-5 h-5 text-gray-400" /> :
                                                    <Eye className="w-5 h-5 text-gray-400" />}
                                            </button>
                                        </div>
                                    </div>

                                    <div className="space-y-2 focus-lift transition-transform duration-200">
                                        <label htmlFor="reset-confirm-password"
                                            className="block text-sm font-medium text-gray-700">Confirm
                                            Password</label>
                                        <div className="relative">
                                            <span className="absolute inset-y-0 left-3 grid place-items-center">
                                                <Lock className="w-5 h-5 text-gray-400" />
                                            </span>
                                            <input
                                                id="reset-confirm-password"
                                                type={passwordShown ? "text" : "password"}
                                                value={forgotPasswordData.confirmPassword}
                                                onChange={(e) => setForgotPasswordData({
                                                    ...forgotPasswordData,
                                                    confirmPassword: e.target.value
                                                })}
                                                className="w-full pl-10 pr-3 py-3 rounded-lg border border-gray-300 outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition"
                                                placeholder="Confirm password"
                                            />
                                        </div>
                                    </div>

                                    <div className="flex space-x-3">
                                        <button
                                            type="button"
                                            onClick={() => setStep('forgot-password')}
                                            className="flex-1 py-3 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition flex items-center justify-center"
                                        >
                                            <ArrowLeft className="h-4 w-4 mr-2" />
                                            Back
                                        </button>
                                        <button
                                            type="button"
                                            onClick={handleResetPasswordWithOTP}
                                            disabled={loading}
                                            className="flex-1 bg-orange-600 hover:bg-orange-700 text-white py-3 px-4 rounded-lg font-semibold transition disabled:opacity-70"
                                        >
                                            {loading ? 'Resetting...' : 'Reset Password'}
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Compliance Marquee */}
                            <div className="mt-6 pt-4 border-t border-gray-200">
                                <div className="marquee-outer text-center text-xs md:text-sm font-medium py-1">
                                    <div className="marquee-track">
                                        <div className="marquee-chunk">
                                            We are aligned with <strong>National Education Policy</strong>, <strong>National
                                                Curriculum Framework</strong>, <strong>Holistic Progress Card Assessment
                                                    Framework</strong>.
                                        </div>
                                        <div className="marquee-chunk">
                                            We are aligned with <strong>National Education Policy</strong>, <strong>National
                                                Curriculum Framework</strong>, <strong>Holistic Progress Card Assessment
                                                    Framework</strong>.
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </main>
        </div>
    );
};

export default AdminLogin;